<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="save">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="max-w-7xl mx-auto md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ $t('AI settings') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/admin/settings">
                            {{ $t('Close') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-12 gap-6">
                    <div class="xl:col-span-2">
                        <div class="bg-white shadow sm:rounded sticky top-4">
                            <div class="p-4">
                                <div class="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-3">{{ $t('Sections') }}</div>
                                <nav class="space-y-1">
                                    <router-link class="block px-3 py-2 text-sm text-gray-800 rounded hover:bg-gray-100" to="/workspace/admin/settings/ai/global-prompts">
                                        {{ $t('Global AI prompts') }}
                                    </router-link>
                                    <router-link class="block px-3 py-2 text-sm text-gray-800 rounded hover:bg-gray-100" to="/workspace/admin/settings/ai/agents/ticket_summary">
                                        {{ $t('Agent (Ticket summary)') }}
                                    </router-link>
                                </nav>
                            </div>
                        </div>
                    </div>
                    <div class="xl:col-span-7 space-y-6">
                <div class="bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">OpenAI API-Anbindung</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">
                                {{ $t('AI integration in this system is always handled via OpenAI.') }}
                                {{ $t('This section combines activation, connection parameters and default language in one central configuration.') }}
                            </div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-5">
                            <div>
                                <label class="stage-filter-label block mb-2" for="ai_enabled">{{ $t('Enable AI integration') }}</label>
                                <input-switch
                                    id="ai_enabled"
                                    v-model="ai_enabled"
                                    :disabled-label="$t('AI is disabled')"
                                    :enabled-label="$t('AI is enabled')"
                                ></input-switch>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="ai_default_locale">Standard Sprache</label>
                                <div class="relative">
                                    <select
                                        id="ai_default_locale"
                                        v-model="ai_default_locale"
                                        class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                    >
                                        <option value="de">Deutsch</option>
                                        <option value="en">English</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="ai_openai_api_key">API-Key</label>
                                <div class="relative">
                                    <input
                                        id="ai_openai_api_key"
                                        v-model="ai_openai_api_key"
                                        autocomplete="off"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        placeholder="sk-..."
                                        type="password"
                                    >
                                </div>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="ai_openai_base_url">API Endpoint</label>
                                <div class="relative">
                                    <input
                                        id="ai_openai_base_url"
                                        v-model="ai_openai_base_url"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        maxlength="255"
                                        placeholder="https://api.openai.com/v1"
                                    >
                                </div>
                            </div>
                            <div class="grid grid-cols-2 gap-4">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="ai_timeout_seconds">Timeout (Sek.)</label>
                                    <div class="relative">
                                        <select
                                            id="ai_timeout_seconds"
                                            v-model.number="ai_timeout_seconds"
                                            class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                        >
                                            <option :value="10">10</option>
                                            <option :value="20">20</option>
                                            <option :value="30">30</option>
                                            <option :value="60">60</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="ai_retry_count">Retry</label>
                                    <div class="relative">
                                        <select
                                            id="ai_retry_count"
                                            v-model.number="ai_retry_count"
                                            class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                        >
                                            <option :value="0">0</option>
                                            <option :value="1">1</option>
                                            <option :value="2">2</option>
                                            <option :value="3">3</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <p class="text-xs text-gray-500 leading-5">
                                Timeout begrenzt die Wartezeit pro API-Call, Retry steuert automatische Wiederholungen bei kurzfristigen Fehlern.
                            </p>
                        </div>
                    </div>
                    <div class="px-6 py-4 border-t flex items-center" style="border-color: var(--ui-border-default);">
                        <a
                            class="text-sm text-gray-500 hover:text-gray-700"
                            href="https://platform.openai.com/settings/organization/billing/overview"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            OpenAI API buchen/aktivieren
                        </a>
                    </div>
                </div>

                <div id="ai-ticket-summary" class="bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Modelle und Antwort-Defaults</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">
                                Definiert globale Standardmodelle.
                                Agent-spezifische Parameter wie Max Output Tokens und Temperature werden direkt am jeweiligen Agenten gesetzt.
                            </div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                            <div>
                                <label class="stage-filter-label block mb-2" for="ai_model_text">Textmodell</label>
                                <div class="relative">
                                    <input
                                        id="ai_model_text"
                                        v-model="ai_model_text"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        maxlength="100"
                                        required
                                    >
                                </div>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="ai_model_embedding">Embedding-Modell</label>
                                <div class="relative">
                                    <input
                                        id="ai_model_embedding"
                                        v-model="ai_model_embedding"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        maxlength="100"
                                        placeholder="text-embedding-3-small"
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Datenschutz-Pseudonymisierung</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">
                                {{ $t('Applies globally to all AI agents.') }}
                                {{ $t('Replaces clear names with aliases before the AI request and restores readable names after the response.') }}
                            </div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <label class="stage-filter-label block mb-2" for="ai_pseudonymization_enabled">Pseudonymisierung aktiv</label>
                            <input-switch
                                id="ai_pseudonymization_enabled"
                                v-model="ai_pseudonymization_enabled"
                                disabled-label="Pseudonymisierung ist deaktiviert"
                                enabled-label="Pseudonymisierung ist aktiviert"
                            ></input-switch>
                        </div>
                    </div>
                </div>

                <div class="bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Antwort-Schema (fest)</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">
                                {{ $t('This response schema is fixed for all AI agents in this application.') }}
                                reply contains the main text.
                                actions, alerts, warnings and notes provide structured lists.
                                links contains reference links as objects with label and url.
                            </div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                            <textarea
                                id="ai_global_response_schema"
                                :value="selectedAiFunctionSchemaPreview"
                                class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5 bg-gray-100 text-gray-700"
                                readonly
                                rows="8"
                                style="height: 300px;"
                            ></textarea>
                            <div>
                                <label class="stage-filter-label block mb-2" for="ai_global_response_schema_prompt">Prompt-Referenz für das feste Antwort-Schema</label>
                                <textarea
                                    id="ai_global_response_schema_prompt"
                                    :value="globalSchemaPromptPreview"
                                    class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5 bg-gray-100 text-gray-700"
                                    readonly
                                    rows="10"
                                    style="height: 300px;"
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                    </div>
                    <div class="xl:col-span-3 space-y-6">
                        <div class="bg-white shadow sm:rounded">
                            <div class="p-6">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">Verbindungstest</h3>
                                <p class="mt-2 text-sm text-gray-500 leading-5">
                                    Prüft die aktuelle OpenAI-Anbindung mit den eingetragenen Verbindungsdaten.
                                </p>
                                <button
                                    :disabled="loading || testingConnection"
                                    class="btn-stage-secondary mt-4"
                                    type="button"
                                    @click="testConnection"
                                >
                                    API-Verbindung testen
                                </button>
                            </div>
                            <div class="px-6 py-4 border-t" style="border-color: var(--ui-border-default);">
                                <div class="text-sm text-gray-700 flex items-center">
                                    Status:
                                    <span
                                        :class="statusDotClass"
                                        class="inline-block ml-2 h-2.5 w-2.5 rounded-full"
                                    ></span>
                                </div>
                                <div v-if="lastConnectionTest.message" class="mt-1 text-xs text-gray-500 leading-4 whitespace-nowrap overflow-hidden text-ellipsis">
                                    {{ lastConnectionTest.message }}
                                </div>
                                <div v-if="lastConnectionTest.at" class="mt-1 text-xs text-gray-500 leading-4">
                                    Letzter Test: {{ lastConnectionTest.at }}
                                </div>
                            </div>
                        </div>
                        <div class="bg-white shadow sm:rounded">
                            <div class="p-6 space-y-4">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">Kosten und Limits</h3>
                                <p class="text-sm text-gray-500 leading-5">
                                    Schutz vor Kosten-Spitzen: Monatsbudget, Warnschwelle und harte Token-Grenzen.
                                    Die Warnschwelle loest eine Fruehwarnung aus, der Hard Stop beendet Requests bei 100% Budget.
                                </p>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="ai_monthly_budget">Monatsbudget ({{ ai_budget_currency }})</label>
                                    <div class="relative">
                                        <input
                                            id="ai_monthly_budget"
                                            v-model.number="ai_monthly_budget"
                                            class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                            min="0"
                                            step="0.01"
                                            type="number"
                                        >
                                    </div>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="ai_budget_currency">Waehrung</label>
                                    <div class="relative">
                                        <select
                                            id="ai_budget_currency"
                                            v-model="ai_budget_currency"
                                            class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                        >
                                            <option value="EUR">EUR</option>
                                            <option value="USD">USD</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="ai_budget_alert_percent">Warnschwelle (%)</label>
                                    <div class="relative">
                                        <select
                                            id="ai_budget_alert_percent"
                                            v-model.number="ai_budget_alert_percent"
                                            class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                        >
                                            <option :value="50">50</option>
                                            <option :value="70">70</option>
                                            <option :value="80">80 (empfohlen)</option>
                                            <option :value="90">90</option>
                                            <option :value="95">95</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="ai_max_tokens_per_request">Max Tokens pro Request</label>
                                    <div class="relative">
                                        <input
                                            id="ai_max_tokens_per_request"
                                            v-model.number="ai_max_tokens_per_request"
                                            class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                            min="256"
                                            step="1"
                                            type="number"
                                        >
                                    </div>
                                </div>
                                <div>
                                    <label class="inline-flex items-center">
                                        <input v-model="ai_hard_stop_enabled" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                                        <span class="ml-2 text-sm text-gray-800">Hard Stop bei Budgetende aktivieren</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: "settings-ai",
    metaInfo() {
        return {
            title: this.$i18n.t('AI settings')
        }
    },
    computed: {
        statusDotClass() {
            if (this.testingConnection) {
                return 'bg-gray-400';
            }
            if (this.lastConnectionTest.ok === true) {
                return 'bg-green-500';
            }
            if (this.lastConnectionTest.ok === false) {
                return 'bg-red-500';
            }
            return 'bg-gray-300';
        },
        selectedAiFunctionSchemaPreview() {
            return JSON.stringify({
                reply: 'string',
                actions: 'string[]',
                alerts: 'string[]',
                warnings: 'string[]',
                notes: 'string[]',
                links: [
                    {
                        label: 'string',
                        url: 'string',
                    }
                ]
            }, null, 2);
        },
        globalSchemaPromptPreview() {
            return [
                'Your response is a valid JSON object in the following immutable response schema:',
                '',
                this.selectedAiFunctionSchemaPreview,
                '',
                'Regeln:',
                '1. "reply" ist der Haupttext deiner Antwort',
                '2. "actions", "alerts", "warnings" und "notes" sind Arrays aus kurzen Strings',
                '3. "links" ist ein Array aus Objekten mit label und url.',
                '4. Felder ohne Inhalt werden als leeres Array zurückgegeben',
                '5. Es werden keine zusätzlichen Felder zurückgegeben',
            ].join('\n');
        }
    },
    data() {
        return {
            loading: true,
            testingConnection: false,
            lastConnectionTest: {
                ok: null,
                message: null,
                at: null,
            },
            ai_enabled: false,
            ai_provider: 'openai',
            ai_default_locale: 'de',
            ai_openai_api_key: '',
            ai_openai_base_url: 'https://api.openai.com/v1',
            ai_timeout_seconds: 20,
            ai_retry_count: 1,
            ai_model_text: 'gpt-5-mini',
            ai_model_embedding: 'text-embedding-3-small',
            ai_pseudonymization_enabled: false,
            ai_prompts_by_group: {},
            ai_function_catalog: [],
            ai_functions_config: {},
            ai_monthly_budget: 50,
            ai_budget_currency: 'EUR',
            ai_budget_alert_percent: 80,
            ai_hard_stop_enabled: true,
            ai_max_tokens_per_request: 8000,
        }
    },
    mounted() {
        this.get();
    },
    methods: {
        ensureAiFunctionConfigEntry(functionKey) {
            const selectedKey = functionKey ? String(functionKey) : null;
            if (!selectedKey) {
                return;
            }
            const meta = (this.ai_function_catalog || []).find((item) => String(item.key) === selectedKey);
            if (!meta) {
                return;
            }

            const defaults = meta.defaults && typeof meta.defaults === 'object'
                ? meta.defaults
                : {};
            const existing = this.ai_functions_config && typeof this.ai_functions_config[selectedKey] === 'object'
                ? this.ai_functions_config[selectedKey]
                : {};
            const normalized = Object.assign({}, defaults, existing);

            normalized.enabled = Boolean(normalized.enabled);
            normalized.prompt_template = normalized.prompt_template ? String(normalized.prompt_template) : null;
            normalized.cache_enabled = Boolean(
                Object.prototype.hasOwnProperty.call(normalized, 'cache_enabled') ? normalized.cache_enabled : true
            );
            if (normalized.cache_ttl_minutes === null || normalized.cache_ttl_minutes === '' || Number(normalized.cache_ttl_minutes) <= 0) {
                normalized.cache_ttl_minutes = null;
            } else {
                normalized.cache_ttl_minutes = Math.min(43200, Math.max(1, Number(normalized.cache_ttl_minutes || 10080)));
            }

            if (!this.ai_functions_config || typeof this.ai_functions_config !== 'object') {
                this.ai_functions_config = {};
            }
            this.ai_functions_config[selectedKey] = normalized;
        },
        testConnection() {
            const self = this;
            self.testingConnection = true;
            axios.post('api/workspace/admin/settings/ai/test-connection', {
                ai_openai_api_key: self.ai_openai_api_key || null,
                ai_openai_base_url: self.ai_openai_base_url || null,
                ai_timeout_seconds: self.ai_timeout_seconds,
                ai_retry_count: self.ai_retry_count,
                ai_model_text: self.ai_model_text,
            }).then(function (response) {
                const details = response.data && response.data.model_count !== undefined
                    ? ' (' + response.data.model_count + ' models found)'
                    : '';
                const warnings = Array.isArray(response.data.warnings) ? response.data.warnings.filter(Boolean) : [];
                const warningText = warnings.length > 0
                    ? ' Notes: ' + warnings.join(' | ')
                    : '';
                const message = (response.data.message || self.$i18n.t('Connection successful.').toString()) + details + warningText;
                self.lastConnectionTest = {
                    ok: true,
                    message: message,
                    at: new Date().toLocaleString('de-DE'),
                };
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: message,
                    type: 'success'
                });
            }).catch(function (error) {
                const message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : self.$i18n.t('Connection test failed.').toString();
                self.lastConnectionTest = {
                    ok: false,
                    message: message,
                    at: new Date().toLocaleString('de-DE'),
                };
                self.$notify({
                    title: self.$i18n.t('Connection failed').toString(),
                    text: message,
                    type: 'error'
                });
            }).finally(function () {
                self.testingConnection = false;
            });
        },
        get() {
            const self = this;
            self.loading = true;
            return axios.get('api/workspace/admin/settings/ai').then(function (response) {
                self.ai_enabled = response.data.ai_enabled === null ? false : response.data.ai_enabled;
                self.ai_provider = response.data.ai_provider || 'openai';
                self.ai_default_locale = response.data.ai_default_locale || 'de';
                self.ai_openai_api_key = response.data.ai_openai_api_key || '';
                self.ai_openai_base_url = response.data.ai_openai_base_url || 'https://api.openai.com/v1';
                self.ai_timeout_seconds = response.data.ai_timeout_seconds === null ? 20 : Number(response.data.ai_timeout_seconds);
                self.ai_retry_count = response.data.ai_retry_count === null ? 1 : Number(response.data.ai_retry_count);
                self.ai_model_text = response.data.ai_model_text || 'gpt-5-mini';
                self.ai_model_embedding = response.data.ai_model_embedding || 'text-embedding-3-small';
                self.ai_pseudonymization_enabled = Boolean(response.data.ai_pseudonymization_enabled);
                self.ai_prompts_by_group = response.data.ai_prompts_by_group && typeof response.data.ai_prompts_by_group === 'object'
                    ? response.data.ai_prompts_by_group
                    : {};
                self.ai_function_catalog = Array.isArray(response.data.ai_function_catalog)
                    ? response.data.ai_function_catalog
                    : [];
                self.ai_functions_config = response.data.ai_functions_config && typeof response.data.ai_functions_config === 'object'
                    ? response.data.ai_functions_config
                    : {};
                self.ai_monthly_budget = response.data.ai_monthly_budget === null ? 50 : Number(response.data.ai_monthly_budget);
                self.ai_budget_currency = response.data.ai_budget_currency || 'EUR';
                self.ai_budget_alert_percent = response.data.ai_budget_alert_percent === null ? 80 : Number(response.data.ai_budget_alert_percent);
                self.ai_hard_stop_enabled = response.data.ai_hard_stop_enabled === null ? true : response.data.ai_hard_stop_enabled;
                self.ai_max_tokens_per_request = response.data.ai_max_tokens_per_request === null ? 8000 : Number(response.data.ai_max_tokens_per_request);
                self.loading = false;
                if (Array.isArray(self.ai_function_catalog)) {
                    self.ai_function_catalog.forEach(function (aiFunction) {
                        self.ensureAiFunctionConfigEntry(aiFunction.key);
                    });
                }
            }).catch(function (error) {
                self.loading = false;
                const message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : self.$i18n.t('AI settings could not be loaded.').toString();
                self.$notify({
                    title: self.$i18n.t('Error while loading').toString(),
                    text: message,
                    type: 'error'
                });
            });
        },
        save() {
            const self = this;
            self.loading = true;
            if (Array.isArray(self.ai_function_catalog)) {
                self.ai_function_catalog.forEach(function (aiFunction) {
                    self.ensureAiFunctionConfigEntry(aiFunction.key);
                });
            }
            axios.post('api/workspace/admin/settings/ai', {
                ai_enabled: self.ai_enabled ? 1 : 0,
                ai_provider: self.ai_provider,
                ai_default_locale: self.ai_default_locale,
                ai_openai_api_key: self.ai_openai_api_key || null,
                ai_openai_base_url: self.ai_openai_base_url || null,
                ai_timeout_seconds: self.ai_timeout_seconds,
                ai_retry_count: self.ai_retry_count,
                ai_model_text: self.ai_model_text,
                ai_model_embedding: self.ai_model_embedding || null,
                ai_pseudonymization_enabled: self.ai_pseudonymization_enabled ? 1 : 0,
                ai_prompts_by_group: self.ai_prompts_by_group,
                ai_functions_config: self.ai_functions_config,
                ai_monthly_budget: self.ai_monthly_budget,
                ai_budget_currency: self.ai_budget_currency,
                ai_budget_alert_percent: self.ai_budget_alert_percent,
                ai_hard_stop_enabled: self.ai_hard_stop_enabled ? 1 : 0,
                ai_max_tokens_per_request: self.ai_max_tokens_per_request,
            }).then(function () {
                return self.get();
            }).then(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Settings updated successfully').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        }
    }
}
</script>
